/**
 * What the turn changed, as one card under the answer.
 *
 * The individual file rows live behind the collapsed step summary, which is the
 * right place for "the agent touched this file at 14:32" and the wrong place for
 * "here is what this turn did to the repository". The card answers the second
 * question: every file, the size of each change, and one way through to the diff.
 *
 * @module components/chat/messages/coding-file-summary
 */

import { FileTextIcon } from "lucide-react";

import { requestDiffPanel } from "@/components/coding/open-diff-panel";
import { cn } from "@/lib/utils";
import type { CodingChangeSummary } from "./coding-turn";

/** Single letter per change kind, in the shape a diff reader already knows. */
const STATUS_LETTER: Record<string, string> = {
  added: "A",
  deleted: "D",
  renamed: "R",
  modified: "M",
  binary: "B",
};

const STATUS_TONE: Record<string, string> = {
  added: "text-success",
  deleted: "text-destructive",
  renamed: "text-muted-foreground",
  modified: "text-muted-foreground",
  binary: "text-muted-foreground",
};

/** Strips the sandbox mount point; mirrors the step rows' `displayPath`. */
function displayPath(path: string): string {
  return path.replace(/^\/workspace\//, "");
}

function Counts({ additions, deletions }: { additions: number; deletions: number }) {
  if (additions === 0 && deletions === 0) return null;

  return (
    <span className="shrink-0 tabular-nums text-xs">
      {additions > 0 && <span className="text-success">+{additions}</span>}
      {additions > 0 && deletions > 0 && <span className="text-muted-foreground/60"> </span>}
      {deletions > 0 && <span className="text-destructive">-{deletions}</span>}
    </span>
  );
}

export function CodingFileSummary({ summary }: { summary: CodingChangeSummary }) {
  const { files, additions, deletions } = summary;
  if (files.length === 0) return null;

  return (
    <div
      data-testid="coding-file-summary"
      className="not-prose my-3 overflow-hidden rounded-lg border border-border"
    >
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <FileTextIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="shrink-0 font-medium text-sm text-foreground">
          {files.length} {files.length === 1 ? "file" : "files"} changed
        </span>
        <Counts additions={additions} deletions={deletions} />
        <button
          type="button"
          data-testid="coding-file-summary-open"
          onClick={requestDiffPanel}
          className={cn(
            "ms-auto shrink-0 text-sm font-medium text-primary",
            "underline-offset-2 transition-colors hover:underline",
          )}
        >
          Open Changes →
        </button>
      </div>

      <ul className="flex flex-col">
        {files.map((file) => (
          <li
            key={file.path}
            data-testid="coding-file-summary-row"
            className="flex items-center gap-3 border-b border-border/60 px-3 py-1.5 last:border-b-0"
          >
            <span
              className={cn(
                "w-3 shrink-0 font-mono text-xs",
                STATUS_TONE[file.status] ?? "text-muted-foreground",
              )}
            >
              {STATUS_LETTER[file.status] ?? "M"}
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
              {displayPath(file.path)}
            </span>
            <Counts additions={file.additions} deletions={file.deletions} />
          </li>
        ))}
      </ul>
    </div>
  );
}
